import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { submitPreSurveyProfile, updateAssessmentStatus } from "../api/client";
import { useTheme } from "../context/ThemeContext";
import {
  EBITDA_OPTIONS,
  REVENUE_OPTIONS,
  WORKSPACE_COMPANY_SIZES,
  WORKSPACE_INDUSTRIES,
} from "../admin/companyWorkspaceOptions";

const MOIC_OPTIONS = [
  "Less than 2x",
  "2.1x – 2.5x",
  "2.6x – 3.0x",
  "3.1x – 4.0x",
  "4.1x – 5.0x",
  "5.1x – 10.0x",
  "More than 10x",
];

const CEO_PE_OPTIONS = [
  "Less than one year",
  "1 – 2 years",
  "2.1 – 3 years",
  "3.1 – 4 years",
  "4.1 – 5 years",
  "5.1 – 6 years",
  "More than 6 years",
];

function SelectField({ label, value, onChange, options, dark, required = true }) {
  return (
    <label className="block">
      <span
        className="block text-[11px] font-semibold uppercase tracking-wider mb-1.5"
        style={{ color: dark ? "rgba(255,255,255,0.55)" : "#64748b" }}
      >
        {label}
        {required ? <span className="text-red-400 ml-0.5">*</span> : null}
      </span>
      <select
        className="w-full rounded-xl px-3.5 py-2.5 text-sm transition-colors focus:outline-none focus:ring-2"
        style={
          dark
            ? {
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.12)",
                color: "#fff",
                focusRing: "rgba(103,232,249,0.35)",
              }
            : {
                background: "#fff",
                border: "1px solid rgba(0,79,115,0.15)",
                color: "#0f172a",
              }
        }
        value={value}
        onChange={onChange}
        required={required}
      >
        <option value="">Select…</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    </label>
  );
}

/** CEO company benchmark — before or after the full assessment. */
export default function PreSurveyPage({
  surveyToken,
  userData,
  companyName: initialCompanyName = "",
  branding = null,
  variant = "before",
  onComplete,
  onSkip,
}) {
  const { theme } = useTheme();
  const dark = theme === "dark";
  const company = userData?.company || {};
  const [companyName, setCompanyName] = useState(
    initialCompanyName || company.company_name || ""
  );
  const [revenueLastYear, setRevenueLastYear] = useState(company.revenue_last_year || "");
  const [industry, setIndustry] = useState(company.industry || "");
  const [companySize, setCompanySize] = useState(company.company_size || "");
  const [ebita, setEbita] = useState(company.ebita || "");
  const [ceoExperience, setCeoExperience] = useState(company.ceoExperience || "");
  const [ceoExperiencePE, setCeoExperiencePE] = useState(company.ceoExperiencePE || "");
  const [exitTransactionsAsCEO, setExitTransactionsAsCEO] = useState(
    company.exitTransactionsAsCEO || ""
  );
  const [saving, setSaving] = useState(false);

  const orgId = useMemo(() => userData?.org_id || "", [userData]);
  const isAfter = variant === "after";

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!companyName.trim()) {
      toast.error("Company name is required.");
      return;
    }
    setSaving(true);
    try {
      await submitPreSurveyProfile(surveyToken, {
        org_id: orgId,
        company_name: companyName.trim(),
        revenue_last_year: revenueLastYear,
        industry,
        company_size: companySize,
        ebita,
        ceoExperience,
        ceoExperiencePE,
        exitTransactionsAsCEO,
        pre_survey: "N",
      });
      if (!isAfter) {
        try {
          await updateAssessmentStatus(surveyToken, "Pending");
        } catch {
          /* continue — benchmark saved */
        }
      }
      onComplete?.();
    } catch (e) {
      toast.error(e.message || "Could not save company profile.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <style>{`
        @keyframes ps-orb {
          0%,100% { transform:translate(0,0) scale(1); }
          50% { transform:translate(24px,-18px) scale(1.06); }
        }
        @keyframes ps-rise {
          from { opacity:0; transform:translateY(12px); }
          to { opacity:1; transform:translateY(0); }
        }
        .ps-rise { animation: ps-rise .4s cubic-bezier(.22,.68,0,1.15) both; }
      `}</style>

      <section
        className="relative min-h-screen overflow-hidden px-4 py-8 sm:py-10"
        style={{
          background: dark
            ? "linear-gradient(135deg,#04080f 0%,#070e1c 50%,#05111f 100%)"
            : "linear-gradient(135deg,#eef2f7 0%,#f8fafc 55%,#e8f0f7 100%)",
        }}
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: dark
              ? "radial-gradient(rgba(255,255,255,0.05) 1px,transparent 1px)"
              : "radial-gradient(rgba(0,79,115,0.10) 1px,transparent 1px)",
            backgroundSize: "28px 28px",
          }}
        />
        <div
          className="absolute pointer-events-none rounded-full"
          style={{
            top: "10%",
            right: "8%",
            width: 320,
            height: 320,
            background: dark
              ? "radial-gradient(circle,rgba(0,79,115,.35) 0%,transparent 70%)"
              : "radial-gradient(circle,rgba(0,79,115,.12) 0%,transparent 70%)",
            animation: "ps-orb 16s ease-in-out infinite",
          }}
        />

        <div className="relative max-w-2xl mx-auto ps-rise">
          <div className="flex items-center gap-2 mb-4">
            {branding?.logo_url ? (
              <img src={branding.logo_url} alt="" className="h-8 w-auto max-w-[120px] object-contain mr-1" />
            ) : null}
            <span
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-widest"
              style={
                dark
                  ? {
                      background: "linear-gradient(135deg,rgba(0,79,115,.55),rgba(26,127,168,.35))",
                      border: "1px solid rgba(26,127,168,.35)",
                      color: "rgba(103,232,249,0.92)",
                    }
                  : {
                      background: "rgba(0,79,115,0.08)",
                      border: "1px solid rgba(0,79,115,0.20)",
                      color: "#004f73",
                    }
              }
            >
              CEO benchmark
            </span>
            <span
              className="text-[10px] font-semibold uppercase tracking-widest"
              style={{ color: dark ? "rgba(255,255,255,0.28)" : "rgba(0,79,115,0.45)" }}
            >
              {isAfter ? "Post-assessment" : "Pre-assessment"}
            </span>
          </div>

          <h1
            className="text-2xl sm:text-3xl font-bold tracking-tight mb-2"
            style={{ color: dark ? "#ffffff" : "#0f172a" }}
          >
            Company benchmark profile
          </h1>
          <p
            className="text-sm leading-relaxed mb-6 max-w-xl"
            style={{ color: dark ? "rgba(255,255,255,0.62)" : "#475569" }}
          >
            {isAfter
              ? "Add or update your company profile for benchmarking. This helps calibrate your results against peers."
              : "Before the full assessment, confirm your company profile. This powers benchmark comparisons in your report."}
          </p>

          <form
            onSubmit={handleSubmit}
            className="rounded-3xl p-5 sm:p-7 space-y-4"
            style={
              dark
                ? {
                    background: "rgba(255,255,255,0.035)",
                    backdropFilter: "blur(24px)",
                    border: "1px solid rgba(255,255,255,0.10)",
                    boxShadow: "0 8px 48px rgba(0,0,0,.45)",
                  }
                : {
                    background: "#ffffff",
                    border: "1px solid rgba(0,79,115,0.12)",
                    boxShadow: "0 8px 40px rgba(0,79,115,.08)",
                  }
            }
          >
            <label className="block">
              <span
                className="block text-[11px] font-semibold uppercase tracking-wider mb-1.5"
                style={{ color: dark ? "rgba(255,255,255,0.55)" : "#64748b" }}
              >
                Company name <span className="text-red-400">*</span>
              </span>
              <input
                type="text"
                className="w-full rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2"
                style={
                  dark
                    ? {
                        background: "rgba(255,255,255,0.06)",
                        border: "1px solid rgba(255,255,255,0.12)",
                        color: "#fff",
                      }
                    : {
                        background: "#fff",
                        border: "1px solid rgba(0,79,115,0.15)",
                        color: "#0f172a",
                      }
                }
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                required
              />
            </label>

            <div className="grid sm:grid-cols-2 gap-4">
              <SelectField
                label="Revenue (current fiscal year)"
                value={revenueLastYear}
                onChange={(e) => setRevenueLastYear(e.target.value)}
                options={REVENUE_OPTIONS}
                dark={dark}
              />
              <SelectField
                label="Industry"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                options={WORKSPACE_INDUSTRIES}
                dark={dark}
              />
              <SelectField
                label="Employees (FTE)"
                value={companySize}
                onChange={(e) => setCompanySize(e.target.value)}
                options={WORKSPACE_COMPANY_SIZES}
                dark={dark}
              />
              <SelectField
                label="EBITDA"
                value={ebita}
                onChange={(e) => setEbita(e.target.value)}
                options={EBITDA_OPTIONS}
                dark={dark}
              />
              <SelectField
                label="MOIC at exit (as CEO)"
                value={exitTransactionsAsCEO}
                onChange={(e) => setExitTransactionsAsCEO(e.target.value)}
                options={MOIC_OPTIONS}
                dark={dark}
              />
              <SelectField
                label="Years of CEO experience"
                value={ceoExperience}
                onChange={(e) => setCeoExperience(e.target.value)}
                options={CEO_PE_OPTIONS}
                dark={dark}
              />
              <SelectField
                label="Years of PE-backed CEO experience"
                value={ceoExperiencePE}
                onChange={(e) => setCeoExperiencePE(e.target.value)}
                options={CEO_PE_OPTIONS}
                dark={dark}
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-2.5 pt-2">
              <button
                type="submit"
                disabled={saving}
                className="flex-1 rounded-2xl py-3.5 text-sm font-bold text-white transition-all disabled:opacity-60"
                style={{
                  background: "linear-gradient(105deg,#004f73 0%,#105a7c 45%,#1a7fa8 55%,#004f73 100%)",
                  boxShadow: dark
                    ? "0 0 24px rgba(0,79,115,.55)"
                    : "0 4px 16px rgba(0,79,115,.30)",
                }}
              >
                {saving ? "Saving…" : isAfter ? "Save benchmark profile" : "Continue to assessment"}
              </button>
              {!isAfter && onSkip ? (
                <button
                  type="button"
                  disabled={saving}
                  onClick={onSkip}
                  className="rounded-2xl py-3.5 px-5 text-sm font-semibold transition-colors"
                  style={
                    dark
                      ? {
                          color: "rgba(255,255,255,0.65)",
                          border: "1px solid rgba(255,255,255,0.14)",
                          background: "rgba(255,255,255,0.04)",
                        }
                      : {
                          color: "#004f73",
                          border: "1px solid rgba(0,79,115,0.20)",
                          background: "rgba(0,79,115,0.04)",
                        }
                  }
                >
                  Skip for now
                </button>
              ) : null}
            </div>
          </form>
        </div>
      </section>
    </>
  );
}
